import React from 'react';
import styled from 'styled-components';
import { useDrop } from 'react-dnd';
import { Icon } from '../../../ui';
import { toRgba } from '../../../ui/theme/colors';
import { ItemTypes } from '../utils/dragTypes';

// Vertical drop zone (between rows)
const VerticalDropZoneContainer = styled.div`
  position: relative;
  width: 100%;
  min-height: ${({ $isVisible }) => ($isVisible ? '60px' : '0px')};
  display: ${({ $isVisible }) => ($isVisible ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0;
  transition: all 0.2s ease;
  pointer-events: auto; /* Allow drag events */
`;

const VerticalDropIndicator = styled.div`
  width: 100%;
  height: ${({ $isOver }) => ($isOver ? '60px' : '50px')};
  border: ${({ $isOver, $themeColor, $borderColor }) => ($isOver ? `3px solid ${$themeColor}` : `2px dashed ${$borderColor}`)};
  border-radius: 8px;
  background: ${({ $isOver, $bgColorHover, $bgColor }) => ($isOver ? $bgColorHover : $bgColor)};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  position: relative;
  pointer-events: auto; /* Ensure this receives drag events */

  &::before {
    content: ${({ $isOver }) => ($isOver ? '"Drop here"' : '""')};
    position: absolute;
    font-size: 12px;
    color: ${({ $themeColor }) => $themeColor};
    font-weight: 500;
    white-space: nowrap;
  }
`;

// Horizontal drop zone (within a row, for side-by-side placement)
const HorizontalDropZoneContainer = styled.div`
  position: relative;
  flex-shrink: 0;
  width: ${({ $isOver }) => ($isOver ? '120px' : '60px')};
  min-height: 70px;
  display: ${({ $isVisible }) => ($isVisible ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 5;
  transition: width 0.2s ease;
  margin: ${({ $position }) => {
    if ($position === 'left') return '0 10px 0 0';
    if ($position === 'right') return '0 0 0 10px';
    return '0 10px';
  }};
`;

const HorizontalDropIndicator = styled.div`
  width: 100%;
  height: 100%;
  min-height: 70px;
  border: ${({ $isOver, $themeColor, $borderColor }) => ($isOver ? `3px solid ${$themeColor}` : `2px dashed ${$borderColor}`)};
  border-radius: 8px;
  background: ${({ $isOver, $bgColorHover, $bgColor }) => ($isOver ? $bgColorHover : $bgColor)};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  transition: all 0.2s ease;
  position: relative;

  /* "Drop here" text when hovering */
  &::after {
    content: ${({ $isOver }) => ($isOver ? '"Drop here"' : '""')};
    position: absolute;
    bottom: 8px;
    font-size: 10px;
    color: ${({ $themeColor }) => $themeColor};
    font-weight: 600;
    white-space: nowrap;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

const DropZoneLabel = styled.span`
  font-size: 11px;
  color: ${({ $themeColor }) => $themeColor};
  font-weight: 500;
  text-transform: uppercase;
  opacity: ${({ $isOver }) => ($isOver ? '0.7' : '1')}; /* Slightly fade when showing "Drop here" */
`;

/**
 * VerticalDropZone - Drop zone between rows for vertical reordering
 */
export const VerticalDropZone = ({ position, onDrop, isVisible, themeColor, onHoverChange }) => {
  // Set up react-dnd drop hook
  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: ItemTypes.MODULE,
      drop: (item) => {
        if (onDrop) {
          onDrop({ ...item, targetPosition: position, type: 'vertical' });
        }
        if (onHoverChange) {
          onHoverChange(false);
        }
        return { position, type: 'vertical' };
      },
      hover: (_item, monitor) => {
        if (monitor.isOver({ shallow: true }) && onHoverChange) {
          onHoverChange(true);
        }
      },
      collect: (monitor) => ({
        isOver: monitor.isOver({ shallow: true }),
        canDrop: monitor.canDrop(),
      }),
    }),
    [position, onDrop, onHoverChange]
  );

  // Compute theme-based colors (same as HorizontalDropZone)
  const borderColor = toRgba(themeColor, 0.4);
  const bgColor = toRgba(themeColor, 0.05);
  const bgColorHover = toRgba(themeColor, 0.1);

  return (
    <VerticalDropZoneContainer ref={drop} $isVisible={isVisible}>
      <VerticalDropIndicator $isOver={isOver && canDrop} $themeColor={themeColor} $borderColor={borderColor} $bgColor={bgColor} $bgColorHover={bgColorHover} />
    </VerticalDropZoneContainer>
  );
};

/**
 * HorizontalDropZone - Drop zone within a row for side-by-side placement
 */
export const HorizontalDropZone = ({ position, rowId, onDrop, isVisible, themeColor }) => {
  // Set up react-dnd drop hook
  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: ItemTypes.MODULE,
      drop: (item) => {
        if (onDrop) {
          onDrop({ ...item, targetRowId: rowId, targetPosition: position, type: 'horizontal' });
        }
        return { rowId, position, type: 'horizontal' };
      },
      collect: (monitor) => {
        // Don't use shallow: true here - we want to detect hover on this specific drop zone
        return {
          isOver: monitor.isOver(),
          canDrop: monitor.canDrop(),
        };
      },
    }),
    [rowId, position, onDrop]
  );

  // Compute theme-based colors
  const borderColor = toRgba(themeColor, 0.4);
  const bgColor = toRgba(themeColor, 0.05);
  const bgColorHover = toRgba(themeColor, 0.1);

  return (
    <HorizontalDropZoneContainer ref={drop} $position={position} $isVisible={isVisible} $isOver={isOver && canDrop}>
      <HorizontalDropIndicator $isOver={isOver && canDrop} $themeColor={themeColor} $borderColor={borderColor} $bgColor={bgColor} $bgColorHover={bgColorHover}>
        <Icon.Add size='small' color={themeColor} />
        <DropZoneLabel $themeColor={themeColor} $isOver={isOver && canDrop}>
          {position}
        </DropZoneLabel>
      </HorizontalDropIndicator>
    </HorizontalDropZoneContainer>
  );
};
